/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import https from 'https';
import uuid from 'uuid';
import getConfig from '../../config';

const LikeToNumber = {
	like: 5,
	hate: 1,
	indifferent: 0,
};

declare let adobeIMS: AdobeIMS;

type ActionMapper = (action: string) => Object;

let debugRunningTotal = 0; // eslint-disable-line no-unused-vars

const ERROR_STATUS_CODE_TAG = 'statusCode:';

export class TrackingService implements ITrackingService {
	// cache: { [resource: string]: TrackInfoItem };

	/**
	 * Perform an HTTP request to the Tracking Server.
	 * @param {object} trackingRequestJson - info about the items sent to the specified Tracking Server endpoint
	 * @param {array} endpoint - the Tracking Server endpoint
	 * @param {array} httpMethod - an HTTP method strike (e.g., 'GET', 'POST', 'PUT')
	 * @returns {Object} tracking request response object
	 */
	_performTrackingRequest(trackingRequestJson: Object | null | undefined, endpoint: string, httpMethod: string): any {
		// TODO: Ask allan about possible responses to this method?
		return new Promise((resolve) => {
			const obj = {
				hostname: getConfig('TRACKING_SERVER'),
				path: endpoint,
				method: httpMethod,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'x-request-id': uuid.v4(),
					'x-api-key': getConfig('CLIENT_ID'),
					authorization: `Bearer ${adobeIMS.getAccessToken()}`,
				},
				dataType: 'json',
				withCredentials: false,
			};
			const request = https.request(obj, (res) => {
				if (res.statusCode && res.statusCode >= 300) {
					// Don't bother logging a single item get that 404's ... only log other error scenarios

					/* istanbul ignore else */
					if (!(httpMethod === 'GET' && res.statusCode === 404)) {
						window.cchome.util.logger.error(
							'Tracking Service',
							'Problem with Tracking Service response',
							res,
						);
					}
					const trackingError = new Error(
						`_performTrackingRequest error, ${ERROR_STATUS_CODE_TAG}${res.statusCode}`,
					);
					resolve(trackingError);
				}
				else {
					let data = '';
					// set the encoding to UTF8 to prevent decoding issues on certain browsers.
					res.setEncoding('utf8');
					res.on('data', (chunk) => {
						data += chunk;
					});
					res.on('end', () => {
						// Success!
						try {
							resolve(JSON.parse(data));
						}
						catch (e) {
							resolve(data);
						}
					});
				}
			});

			if (trackingRequestJson) {
				const postBody = JSON.stringify(trackingRequestJson);
				request.write(postBody);
			}

			request.on('error', (err) => {
				// There was a connection error of some sort
				window.cchome.util.logger.error(
					'Tracking Service',
					'Problem with Tracking Service request',
					err.toString(),
				);
				const trackingError = new Error('_performTrackingRequest error');
				resolve(trackingError);
			});

			request.end();
		});
	}

	/**
	 * Returns a track info object for the specified resource/category pair.
	 * If the resource is not yet Tracked by the Tracking Server, this will CREATE a new Track for it on the server.
	 * This will also cache the item in our memory cache.
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns Track info for the resource corresponding to the specified input, or 'undefined' if failed
	 */
	async _getOrCreateTrackInfo(resourceCategoryPair: ResourceCategoryPair): Promise<TrackInfoItem | null | undefined> {
		if (this._validateResourceCategoryArray([resourceCategoryPair]) === false) {
			return undefined;
		}

		// See if we already have it in the cache...if so, we'll simply return it
		let resultItem: TrackInfoItem | null | undefined = TrackingService.cacheInstance[resourceCategoryPair.resource];

		if (!resultItem || !resultItem.existsOnServer) {
			// IF...
			//   The item is not cached (meaning we have no idea whether the item is on the server) --
			// OR...
			//    We previously verified that the item does not exist on the server (and we cached a dummy track for the item),
			//    and mindful that some other client might have snuck in a Create since that time --
			// THEN...
			//    1) Do a GET on the server.
			//    2) If the GET fails, do a CREATE on the server.

			resultItem = undefined;

			// Call server with an array of one resource to convert to a trackId

			const trackIdsResult = await this._performTrackingRequest(
				{resources: [resourceCategoryPair.resource]},
				'/v2/trackids',
				'POST',
			);

			if (!(trackIdsResult instanceof Error)) {
				// Call server with a trackId to try to get an existing track's info

				const getResult = await this._performTrackingRequest(
					undefined,
					`/v2/tracks/${trackIdsResult.trackIds[0]}`,
					'GET',
				);

				if (!(getResult instanceof Error)) {
					// Item exists on the server...process it prior to returning it

					resultItem = this._processTrackInfoFromServer(getResult);
				}
				else {
					// Some kind of read error (which is not a bad thing if it's a 404)

					// If the error has a 404 statusCode (meaning the track does not exist on the server), then craete a new track on the server
					if (this._getServerErrorStatusCode(getResult) === 404) {
						// A track for this resource does not exist on the server...create it

						// Log the tally of track creates
						debugRunningTotal += 1;
						const debugStr = `(create) - this: 1, total: ${debugRunningTotal}, `;
						this._logMessage(debugStr, resourceCategoryPair);

						const createResult = await this._performTrackingRequest(
							{resource: resourceCategoryPair.resource, category: resourceCategoryPair.category},
							'/v2/tracks',
							'POST',
						);

						if (!(createResult instanceof Error)) {
							// Item successfully created on the server...process it prior to returning it

							resultItem = this._processTrackInfoFromServer(createResult);
						}
						else {
							// Some kind of create error

							// If the error has a 303 statusCode (meaning the track already exists on the server...this shouldn't occur...seems like a bug)
							if (this._getServerErrorStatusCode(createResult) === 303) {
								window.cchome.util.logger.error(
									'Tracking Service',
									'Problem trying to create over an existing track in _getOrCreateTrackInfo',
								);
							}
							else {
								// Some other kind of create error
								window.cchome.util.logger.error(
									'Tracking Service',
									'Problem creating track in _getOrCreateTrackInfo',
								);
							}
						}
					}
					else {
						// Some other kind of read error
						window.cchome.util.logger.error(
							'Tracking Service',
							'Problem reading track in _getOrCreateTrackInfo',
						);
					}
				}
			}
			else {
				// Some kind of error getting a trackId for the resource
				window.cchome.util.logger.error(
					'Tracking Service',
					'Problem getting trackId for resource in _getOrCreateTrackInfo',
				);
			}
		}
		else {
			// We know that the item exists on the server because it's in our cache
			// So, we simply return the cached item that we already have for it
		}

		return resultItem;
	}

	// end private interface functions

	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	/**
	 * Returns array of objects containing track info for each resource in the specified array.
	 * This will also cache the item in our memory cache.
	 * This will NOT perform creates.
	 * @param {array} inArray - an array of resource-category pairs
	 * @returns Array of objects containing track info for each resource in the specified array, or 'undefined' if failed
	 */
	async getTrackInfo(inArray: ResourceCategoryArray): Promise<TrackInfoArray> {
		if (this._validateResourceCategoryArray(inArray) === false) {
			return [];
		}

		// Will return the retrieved Track info for the specified array
		const outputArray: Array<TrackInfoItem> = [];
		const resourceArray: Array<ResourceCategoryPair> = [];

		inArray.forEach((resourceCategoryPair) => {
			// Populate output array with a list of items. The items will either be
			// resource info objects from the cache or undefined. The reason the 'undefines'
			// are added is that if there are items to go fetch, I go through the results
			// of that fetch and stuff them into the 'undefined' slots in order.
			// Each undefined item will also go into the array of items to go fetch.
			const item = TrackingService.cacheInstance[resourceCategoryPair.resource];

			outputArray.push(item);

			if (item === undefined) {
				resourceArray.push(resourceCategoryPair); // We'll get a track for this on the server
			}
		}, this);

		// This array will be non-empty if there are items that need to be read from the server
		if (resourceArray.length > 0) {
			// Log the tally of track reads
			debugRunningTotal += resourceArray.length;
			const debugStr = `(get) - this: ${resourceArray.length}, total: ${debugRunningTotal}, `;
			this._logMessage(debugStr, resourceArray);

			// Create the body for the batch GET
			const resourceArrayToGetForPost = resourceArray.map(resourceCategoryPair => ({
				method: 'GET',
				resource: resourceCategoryPair.resource,
			}));

			// Batch GET the tracks
			// 'response' can contain a mix of success and error items
			// An item that errored is primarily indicative of an untracked resource...in that case, we locally create a dummy track info for it
			const response = await this._performTrackingRequest(
				{batch: resourceArrayToGetForPost},
				'/v2/tracks/batch',
				'POST',
			);

			if (!(response instanceof Error)) {
				let outputIndex = 0;

				response.batch.forEach((newItem, index) => {
					const goodItem = newItem.code >= 200 && newItem.code < 300;

					// If a track for this resource was successfully read from the server
					if (goodItem) {
						newItem.body = this._processTrackInfoFromServer(newItem.body);
					} // Else this resource is not tracked on the server...create/cache/return a dummy TrackInfo for it
					else {
						const dummyTrackInfo: TrackInfoItem = {
							resource: resourceArray[index].resource,
							category: resourceArray[index].category,
							trackId: 'DUMMY_TRACK_ID',
							viewed: 0,
							interacted: 0,
							viewedTime: 0,
							interactedTime: 0,
							shared: 0,
							liked: 'indifferent',
							completionStatus: 'none',
							completionStatusTime: 0,
							existsOnServer: false, // So that a later create will know that a track for this needs to be created on the server
						};
						newItem.code = 200;
						newItem.body = dummyTrackInfo;
						this._cacheItem(dummyTrackInfo);
					}

					// Insert item into the first empty slot in our output array
					outputIndex = this._findNextEmpty(outputArray, outputIndex);
					if (outputIndex !== -1) {
						// If this is a valid item, add it to the output array
						// If this is a bad item, then leave undefined in this array slot
						// if (goodItem && (outputIndex % 2 === 0)) {
						outputArray[outputIndex] = newItem.body;
						outputIndex++;
					}
					else {
						// Shouldn't fail, but log this if we happen to run into this
						window.cchome.util.logger.error(
							'Tracking Service',
							'Problem with _findNextEmpty in getTrackInfo',
						);
					}
				});
			}
			else {
				// FIXME
				// _performTrackingRequest failed ... what should we do?
				// outputArray will contain a mix of 1) trackinfo for cached items and 2) undefined for resources not in the cache
			}
		}

		return outputArray;
	}

	/**
	 * Gets the TrackInfoItem cache
	 * @returns {Object} - the TrackInfoItem cache
	 */
	getCache(): Object {
		return TrackingService.cacheInstance;
	}

	/**
	 * Gets the `interacted` count for the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {ValueTimePair} - the track's 1) interacted count, and 2) last interacted time...undefined if not in cache
	 */
	async getInteracted(resourceCategoryPair: ResourceCategoryPair): Promise<ValueTimePair | null | undefined> {
		const item: TrackInfoItem | null | undefined
			= TrackingService.cacheInstance[resourceCategoryPair.resource]
			|| (await this.getTrackInfo([resourceCategoryPair]))[0];
		return item ? {value: item.interacted, time: item.interactedTime} : undefined;
	}

	/**
	 * Gets the `viewed` count for the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {ValueTimePair} - the track's 1) viewed count, and 2) last viewed time...undefined if the resource is not in cache
	 */
	async getViewed(resourceCategoryPair: ResourceCategoryPair): Promise<ValueTimePair | null | undefined> {
		const item: TrackInfoItem | null | undefined
			= TrackingService.cacheInstance[resourceCategoryPair.resource]
			|| (await this.getTrackInfo([resourceCategoryPair]))[0];
		return item ? {value: item.viewed, time: item.viewedTime} : undefined;
	}

	/**
	 * Gets the `shared` count for the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {number} - the track's shared count...undefined if the resource is not in cache
	 */
	async getShared(resourceCategoryPair: ResourceCategoryPair): Promise<number | null | undefined> {
		const item: TrackInfoItem | null | undefined
			= TrackingService.cacheInstance[resourceCategoryPair.resource]
			|| (await this.getTrackInfo([resourceCategoryPair]))[0];
		return item ? item.shared : undefined;
	}

	/**
	 * Get the `like` status of the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {LikeStatus} - The track's like status...undefined if the resource is not in cache
	 */
	async getLiked(resourceCategoryPair: ResourceCategoryPair): Promise<LikeStatus | null | undefined> {
		const item: TrackInfoItem | null | undefined
			= TrackingService.cacheInstance[resourceCategoryPair.resource]
			|| (await this.getTrackInfo([resourceCategoryPair]))[0];
		return item ? item.liked : undefined;
	}

	/**
	 * Gets the completionStatus for the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {CompletionStatusTimePair} - the track's CompletionStatusTimePair...undefined if the resource is not in cache
	 */
	async getCompletionStatus(
		resourceCategoryPair: ResourceCategoryPair,
	): Promise<CompletionStatusTimePair | null | undefined> {
		const item: TrackInfoItem | null | undefined
			= TrackingService.cacheInstance[resourceCategoryPair.resource]
			|| (await this.getTrackInfo([resourceCategoryPair]))[0];
		return item ? {status: item.completionStatus, time: item.completionStatusTime} : undefined;
	}

	/**
	 * Increments the 'interacted' count of the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {boolean} - false if the resource is not in cache
	 */
	async setInteracted(resourceCategoryPair: ResourceCategoryPair): Promise<boolean> {
		return await this._setAction('interacted', resourceCategoryPair, this._actionFunc);
	}

	/**
	 * Increments the 'viewed' count of the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {boolean} - false if the resource is not in cache
	 */
	async setViewed(resourceCategoryPair: ResourceCategoryPair): Promise<boolean> {
		return await this._setAction('viewed', resourceCategoryPair, this._actionFunc);
	}

	/**
	 * Sets the completion status for the resource to one of the CompletionStatus states
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {CompletionStatus} - the staus to set the resource to
	 */
	async setCompletionStatus(resourceCategoryPair: ResourceCategoryPair, status: CompletionStatus): Promise<boolean> {
		let completionFunc;

		switch (status) {
			case 'partial':
				completionFunc = this._partiallyCompletedFunc;
				break;

			case 'complete':
				completionFunc = this._completedFunc;
				break;

			case 'none':
			default:
				completionFunc = this._noneCompletedFunc;
				break;
		}
		return await this._setAction('completionStatus', resourceCategoryPair, completionFunc);
	}

	/**
	 * Increments the 'shared' count of the resource
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @returns {boolean} - false if the resource is not in cache
	 */
	async setShared(resourceCategoryPair: ResourceCategoryPair): Promise<boolean> {
		return await this._setAction('shared', resourceCategoryPair, this._actionFunc);
	}

	/**
	 * Sets the 'liked' status of the resource to the corresponding LikeStatus.
	 * The value for `likeStatus` is a string such as 'like' or 'hate'
	 * @param {ResourceCategoryPair} resourceCategoryPair - pair containing a resournce and a category
	 * @param {LikeStatus} likeStatus - the status to set on this resource
	 * @returns {boolean} - false if the resource is not in cache
	 */
	async setLikeStatus(resourceCategoryPair: ResourceCategoryPair, likeStatus: LikeStatus): Promise<boolean> {
		let likeFunc;

		switch (likeStatus) {
			case 'like':
				likeFunc = this._likeFunc;
				break;

			case 'hate':
				likeFunc = this._hateFunc;
				break;

			case 'indifferent':
			default:
				likeFunc = this._indifferentFunc;
				break;
		}
		return await this._setAction('liked', resourceCategoryPair, likeFunc);
	}

	/*
	 **
	 **  S T A T I C   I N S T A N C E S
	 **
	 */
	static cacheInstance: Object;

	/*
	 **
	 **  P R I V A T E   M E T H O D S
	 **
	 */

	/**
	 * CONSTRUCTOR
	 * Enforces a singleton track info cache
	 */
	constructor() {
		if (!TrackingService.cacheInstance) {
			TrackingService.cacheInstance = {};
		}
	}

	_actionFunc(theAction: string): Object {
		return {
			action: theAction,
		};
	}

	_completedFunc(): Object {
		return {
			action: 'completionStatus',
			value: 'complete',
		};
	}

	_partiallyCompletedFunc(): Object {
		return {
			action: 'completionStatus',
			value: 'partial',
		};
	}

	_noneCompletedFunc(): Object {
		return {
			action: 'completionStatus',
			value: 'none',
		};
	}

	_likeFunc(): Object {
		return {
			action: 'liked',
			value: LikeToNumber.like,
		};
	}

	_hateFunc(): Object {
		return {
			action: 'liked',
			value: LikeToNumber.hate,
		};
	}

	_indifferentFunc(): Object {
		return {
			action: 'liked',
			value: LikeToNumber.indifferent,
		};
	}

	/**
	 * CCHome only supports 3 like values, like(5), not-like(1), and neutral(0).
	 * however the server supports values from 0-4 and we want to take into account
	 * that some other client may set values as "stars" using the full 6 number range
	 * To ensure we work, we will map 0 => 0, 1&2 => 1, 3&4&5 => 5
	 * @param {number} val - the value coming from the server
	 * @returns {LikeStatus} - one of the accepted "like" values.
	 */
	_likeMapper(val: number): LikeStatus {
		let retVal: LikeStatus;
		switch (val) {
			case 1:
			case 2:
				retVal = 'hate';
				break;

			case 3:
			case 4:
			case 5:
				retVal = 'like';
				break;

			case 0:
			default:
				retVal = 'indifferent';
				break;
		}

		return retVal;
	}

	/**
	 * Helper to indicates that the specified actions's count/like for the specified resource should be incremented/updated.
	 * @param {string} theAction - supported values are 'interacted', 'viewed', 'shared', 'liked'
	 * @param {string} resource - a resource
	 * @param {ActionMapper} theFunc - ActionMapper, for making the PUT request's body
	 * @returns {booean} true if the track has already been cached and is successfully updated, else false
	 */
	async _setAction(
		theAction: string,
		resourceCategoryPair: ResourceCategoryPair,
		theFunc: ActionMapper,
	): Promise<boolean> {
		let result = false;

		// See if it's in the cache
		let item = TrackingService.cacheInstance[resourceCategoryPair.resource];

		// If it's NOT in the cache, OR it's a a DUMMY track info in the cache, create it on the Server
		if (!item || !item.existsOnServer) {
			item = await this._getOrCreateTrackInfo(resourceCategoryPair);
		}

		if (item) {
			// Prepare the PUT resource array for the POST body
			const resourceToPutForPost = theFunc(theAction);

			const trackId = item.trackId;

			// Log the track put
			this._logMessage('(put) - this: 1', [resourceCategoryPair, resourceToPutForPost]);

			const putResult = await this._performTrackingRequest(resourceToPutForPost, `/v2/tracks/${trackId}`, 'PUT');

			if (!(putResult instanceof Error)) {
				this._processTrackInfoFromServer(putResult);

				result = true;
			}
			else {
				result = false;
			}
		}
		else {
			// Unexpected: This TrackInfoItem is undefined, meaning _getOrCreateTrackInfo failed to create a track on the server
			this._logMessage('undefined TrackInfoItem in _setAction', undefined);
		}

		return result;
	}

	/**
	 * Removes everything from the cache.
	 * @returns void
	 */
	_clearCache() {
		TrackingService.cacheInstance = {};
	}

	/**
	 * Removes the specified resource from the cache
	 * @param {string} resource - a resource
	 * @returns void
	 */
	_removeCacheItem(resource: string) {
		/* istanbul ignore else */
		if (resource) {
			delete TrackingService.cacheInstance[resource];
		}
	}

	/**
	 * Caches a single TrackInfoItem
	 * @param {trackInfo} trackInfoItem - a track info item
	 * @returns void
	 */
	_cacheItem(trackInfoItem: TrackInfoItem) {
		/* istanbul ignore else */
		if (trackInfoItem) {
			TrackingService.cacheInstance[trackInfoItem.resource] = trackInfoItem;
		}
	}

	/**
	 * Searches the info array, starting at startIndex, looking for an 'undefined'
	 * item and returns that index.
	 * @param {array} trackInfoArray - an array of track info items
	 * @param {integer} startIndex - the index in the array to start searching
	 * this item can be 'undefined'
	 * @returns {integer} the index the item was inserted into, or -1 if not found
	 */
	_findNextEmpty(trackInfoArray: TrackInfoArray, startIndex: number): number {
		let index = startIndex === undefined ? 0 : startIndex;

		// if index is out of bounds, just return -1
		if (trackInfoArray === undefined || index < 0 || index >= trackInfoArray.length) {
			return -1;
		}

		// if start index is undefined then start searching from the beginning
		// otherwise start at the indicated index
		let done = false;

		do {
			if (trackInfoArray[index] === undefined) {
				done = true;
			}
			else {
				index++;

				// done will be true if we have hit the end of the array
				done = index === trackInfoArray.length;
				if (done) {
					index = -1; // getting here means we didn't insert, return -1
				}
			}
		} while (!done);

		return index;
	}

	/**
	 * Validates the ResourceCategoryArray.
	 * @param {resourceCategoryArray} resourceCategoryArray - an array of resource-category pairs
	 * @returns Boolean: true if valid, else false
	 */
	_validateResourceCategoryArray(resourceCategoryArray: ResourceCategoryArray) {
		let result = false;

		if (resourceCategoryArray === undefined || resourceCategoryArray.length <= 0) {
			// Do we have a valid array?

			this._logMessage('_validateResourceCategoryArray: invalid resourceCategoryArray', undefined);
		}
		else {
			// Is each item in the array a valid ResourceCategoryPair?

			try {
				let badResourceCount = 0;
				let badCategoryCount = 0;

				resourceCategoryArray.forEach((resourceCategoryPair) => {
					if (
						resourceCategoryPair.resource === undefined
						|| typeof resourceCategoryPair.resource !== 'string'
						|| resourceCategoryPair.resource.length < 1
					) {
						this._logMessage(
							'_validateResourceCategoryArray: bad resource: ',
							resourceCategoryPair.resource,
						);
						badResourceCount += 1;
					}
					if (
						resourceCategoryPair.category === undefined
						|| typeof resourceCategoryPair.category !== 'string'
						|| [
							'playlist',
							'tutorial',
							'test',
							'opus-project',
							'opus-project-step',
							'opus-skill',
							'opus-resource',
							'todo',
							'upsell',
							'web-apphomes-banner',
						].indexOf(resourceCategoryPair.category) === -1
					) {
						this._logMessage(
							'_validateResourceCategoryArray: bad category: ',
							resourceCategoryPair.category,
						);
						badCategoryCount += 1;
					}
				});

				result = badResourceCount === 0 && badCategoryCount === 0;
			}
			catch (err) {
				// 'result' is left unchanged as false
			}
		}

		return result;
	}

	/**
	 * Process a TrackInfoItem received from the server.
	 * likemap it, remove the uesrId, set its `existsOnServer` property to true, add it to the cache.
	 * Return the processed TrackInfoItem.
	 * @param {any} trackInfoItem - TrackInfoItem from server
	 * @returns {TrackInfoItem} - processed TrackInfoItem
	 */
	_processTrackInfoFromServer(trackInfoItem: any) {
		let resultTrackInfoItem: TrackInfoItem | null | undefined = undefined;

		// Clean it up, cache it, and set it as the return value

		/* istanbul ignore else */
		if (trackInfoItem) {
			// LikeMap the liked property before caching it
			trackInfoItem.liked = this._likeMapper(trackInfoItem.liked);

			// Remove the `userId` property here, since we don't want to include it in the cached data
			delete trackInfoItem.userId;

			// Set a property to indicate that a this track exists on the server
			trackInfoItem.existsOnServer = true;

			// Add the newly-tracked item to the cache
			this._cacheItem(trackInfoItem);

			resultTrackInfoItem = trackInfoItem;
		}

		return resultTrackInfoItem;
	}

	/**
	 * Log the message to the console with a prefix
	 * @param {string} message - message to log
	 * @param {any} otherData - optional data object to log
	 * @returns {void}
	 */
	_logMessage(message: string, otherData: any) {
		/* istanbul ignore else */
		if (message) {
			/* istanbul ignore else */
			if (otherData) {
				window.cchome.util.logger.log('Tracking Service', `TRK: ${message}`, otherData);
			}
			else {
				window.cchome.util.logger.log('Tracking Service', `TRK: ${message}`);
			}
		}
	}

	/**
	 * Gets the statusCode from the Error, if any
	 * @param {Error} err - an error object possibly containing a status code in its `message` property
	 * @returns {number} statusCode or undefined
	 */
	_getServerErrorStatusCode(err: Error) {
		// See if it has a status code embedded in the error object's message
		let statusCode: undefined | string | number = undefined;
		const errorMsg = err.message;
		const statusCodeTagIndex = errorMsg.indexOf(ERROR_STATUS_CODE_TAG);
		if (statusCodeTagIndex !== -1) {
			statusCode = errorMsg.substr(statusCodeTagIndex + ERROR_STATUS_CODE_TAG.length);
			statusCode = parseInt(statusCode) || undefined;
		}
		return statusCode;
	}
}

export default TrackingService;
